import React, { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useLiffProfile } from "../../context/LiffProfileContext";
import LoadingBar from "../LoadingBar";
import ErrorModal from "../modals/ErrorModal";
import DangerButton from "../common/DangerButton";
import Button from "../common/Button";
import ScanIcon from "../../assets/images/scan-icon.png";
import RegisterResultForm from "./RegisterResultForm";
import ScanIdCard from "../user/ScanIdCard";
import ScanIdBackCard from "../user/ScanIdBackCard";
import PdpaConsentRegister from "../modals/pdpaConsentRegister";
import OtpFormRegister from "../otp/OtpFormRegister";
import TermRegister from "../modals/termRegister";
import PrivacyPolicyRegister from "../modals/privacyPolicyRegister";
import TooltipCard from "../modals/TooltipCard";
import DropdownDatepicker from "../common/DropdownDatePicker";
import RadioGroup from "../common/Radio";
import ScanIdFrontCard from "../user/ScanIdFrontCard";

const FormItem = ({ label, children, onClose, onAccept, error }) => (
  <div className="flex flex-col gap-1">
    <label className="text-base font-medium text-left">{label}</label>
    {children}
    {error && (
      <span className="text-red-500 text-sm mt-1 text-left">{error}</span>
    )}
  </div>
);

const Input = ({
  name,
  value,
  placeholder,
  onChange,
  type = "text",
  maxLength,
  required,
}) => (
  <input
    className="h-11 text-lg font-normal rounded-lg border px-3 w-full"
    name={name}
    value={value}
    placeholder={placeholder}
    onChange={onChange}
    type={type}
    maxLength={maxLength}
    required={required}
  />
);

const formatLaserCode = (input) => {
  // เอาเฉพาะอักษร/เลข (2 ตัวแรกอักษร/เลข, ที่เหลือเลข)
  let raw = input.toUpperCase().replace(/[^A-Z0-9]/g, "");

  // บังคับให้ตัวที่ 3 ขึ้นไปเป็นเลข
  if (raw.length > 2) {
    // ตัด 2 ตัวแรกออก (prefix)
    let prefix = raw.slice(0, 2);
    // filter suffix เฉพาะเลข
    let suffix = raw.slice(2).replace(/[^0-9]/g, "");
    raw = prefix + suffix;
  }

  // ตัดความยาวสูงสุด 12 ตัว
  raw = raw.slice(0, 12);

  // ดึง segment ตาม format JT1-2345678-91
  const seg1 = raw.slice(0, 3); // JT1
  const seg2 = raw.slice(3, 10); // 2345678
  const seg3 = raw.slice(10, 12); // 91

  // สร้าง formatted string
  let formatted = seg1;
  if (seg2) formatted += `-${seg2}`;
  if (seg3) formatted += `-${seg3}`;

  // เช็คเฉพาะเมื่อครบความยาวทั้งหมด
  if (raw.length === 12) {
    // 2 ตัวแรกอักษร/เลข, ตัวที่ 3 อักษร/เลข, ตัวที่ 4-12 ต้องเป็นเลข (ตามฟอร์แมต JT1-2345678-91)
    // จริงๆ JT1 (seg1) = 2 ตัวแรกอักษร/เลข + 1 ตัวเลข
    if (
      !/^[A-Z0-9]{2}[0-9]{1}$/.test(seg1) || // JT1
      !/^[0-9]{7}$/.test(seg2) ||
      !/^[0-9]{2}$/.test(seg3)
    ) {
      return {
        valid: false,
        formatted,
        raw,
        error: "รูปแบบต้องเป็น JT1-2345678-91 เท่านั้น",
      };
    }
    return { valid: true, formatted, raw, error: "" };
  }

  // ยังไม่ครบ 12 ตัว ให้ user พิมพ์ต่อได้
  return { valid: null, formatted, raw, error: "" };
};

export default function Register() {
  const { t, i18n } = useTranslation();
  const { authToken, userId } = useLiffProfile();
  const location = useLocation();
  const cardData = location.state?.cardData;
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  //const [otpPayload, setOtpPayload] = useState(null);
  const [birthDateItem, setBirthDateItem] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showOtp, setShowOtp] = useState(false);
  const [showPdpaModal, setShowPdpaModal] = useState(false);
  const [showTermModal, setShowTermModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [pdpaPayload, setPdpaPayload] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [hasCheckedIdCard, setHasCheckedIdCard] = useState(false);
  const [displayLaser, setDisplayLaser] = useState("");
  const [triggerOtpFromSubmit, setTriggerOtpFromSubmit] = useState(false);
  const [scanSide, setScanSide] = useState("front");
  const [birthDateType, setBirthDateType] = useState(1);
  const birthDateTypes = [
    { label: t("day_month_year"), value: 1 },
    { label: t("month_year"), value: 2 },
    { label: t("year"), value: 3 },
  ];

  const [registerData, setRegisterData] = useState({
    idCard: "",
    firstName: "",
    lastName: "",
    gender: "",
    birthDate: "",
    laser: "",
    mobilePhone: "",
    email: "",
    issuedStore: "",
    consentStatus: "N",
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const inputValue =
      name === "mobilePhone" ? value.replace(/\D/g, "") : value;
    setRegisterData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (name === "idCard") {
      const digitsOnly = inputValue.replace(/\D/g, "").slice(0, 13);

      setRegisterData((prev) => ({ ...prev, idCard: digitsOnly }));

      if (!digitsOnly) {
        setFormErrors((prev) => ({
          ...prev,
          idCard: t("form_item_0_cannot_be_empty", { field: t("citizen_id") }),
        }));
      } else if (digitsOnly.length !== 13) {
        setFormErrors((prev) => ({
          ...prev,
          idCard: t("form_item_invalid_citizen_id_format"),
        }));
      } else {
        setFormErrors((prev) => ({ ...prev, idCard: "" }));
      }

      return;
    }

    if (name === "firstName") {
      if (!/^[\u0E00-\u0E7F]+$/.test(inputValue)) {
        setFormErrors((prev) => ({
          ...prev,
          firstName: t("only_thai_characters_allowed"),
        }));
      } else {
        setFormErrors((prev) => ({ ...prev, firstName: "" }));
      }
    }

    if (name === "lastName") {
      if (!/^[\u0E00-\u0E7F]+$/.test(inputValue)) {
        setFormErrors((prev) => ({
          ...prev,
          lastName: t("only_thai_characters_allowed"),
        }));
      } else {
        setFormErrors((prev) => ({ ...prev, lastName: "" }));
      }
    }
    if (name === "gender") {
      setFormErrors((prev) => ({
        ...prev,
        gender: value
          ? ""
          : t("form_item_0_cannot_be_empty", { field: t("gender") }),
      }));
    }
    if (name === "birthDate") {
      setFormErrors((prev) => ({
        ...prev,
        birthDate: value
          ? ""
          : t("form_item_0_cannot_be_empty", {
              field: t("birth_date_on_citizen_id_card"),
            }),
      }));
    }

    if (name === "mobilePhone") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);

      setRegisterData((prev) => ({
        ...prev,
        mobilePhone: digitsOnly,
      }));

      if (!/^\d{10}$/.test(digitsOnly)) {
        setFormErrors((prev) => ({
          ...prev,
          mobilePhone: t("form_item_invalid_mobile_number_format"),
        }));
      } else {
        setFormErrors((prev) => ({ ...prev, mobilePhone: "" }));
      }

      return;
    }

    if (name === "email") {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(inputValue)) {
        setFormErrors((prev) => ({
          ...prev,
          email: t("form_item_invalid_email_format"),
        }));
      } else {
        setFormErrors((prev) => ({ ...prev, email: "" }));
      }
    }
    if (name === "laser") {
      const result = formatLaserCode(value);
      setDisplayLaser(result.formatted);

      // ถ้าครบ 12 ตัวและไม่ผ่าน format → ไม่อัปเดต state + แสดง error
      if (result.valid === false) {
        //setLaserError(result.error);
        setFormErrors((prev) => ({
          ...prev,
          laser: "AA#-#######-##",
        }));
        // ไม่อัปเดต registerData.laser
        return;
      } else {
        setFormErrors((prev) => ({ ...prev, laser: "" }));
        setRegisterData((prev) => ({
          ...prev,
          laser: result.raw,
        }));
      }

      return;
    }

    if (name === "pdpa") {
      setRegisterData((prev) => ({
        ...prev,
        pdpa: checked, // ถ้า "C" checked, ถ้า "N" ไม่ checked
        consentStatus: checked ? "C" : "N",
      }));
    }
    if (name === "terms") {
      setRegisterData((prev) => ({
        ...prev,
        terms: !!checked,
        contactPermission: checked ? 1 : 2, // 1=Allow, 2=Not Allow
        contactPreference: checked ? 7 : 0, // 0=Not Allow, 7=Email+Mobile+Mail
      }));
    }
  };

  /*
  useEffect(() => {
    if (otpPayload && triggerOtpFromSubmit) {
      setShowOtp(true);
      setTriggerOtpFromSubmit(false);
    }
  }, [otpPayload, triggerOtpFromSubmit]);*/

  useEffect(() => {
    if (cardData) {
      // console.log("Payload ที่รับมาจาก Scan:", cardData);
    }
  }, [cardData]);

  const handleDateChange = (e) => {
    const inputDate = e.target.value;
    setBirthDateItem(inputDate);
    setRegisterData((prev) => ({ ...prev, birthDate: inputDate }));

    setFormErrors((prev) => ({
      ...prev,
      birthDate: inputDate
        ? ""
        : t("form_item_0_cannot_be_empty", {
            field: t("birth_date_on_citizen_id_card"),
          }),
    }));
  };

  const formatCitizenId = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 13);
    return digits
      .replace(/^(\d{1})(\d{4})(\d{5})(\d{2})(\d{1})$/, "$1 $2 $3 $4 $5")
      .trim();
  };

  const formatBirthDate = (val, type) => {
    if (!val?.year) return "";

    const year = String(parseInt(val.year, 10));
    const month = val.month ? String(val.month).padStart(2, "0") : "00";
    const day = val.day ? String(val.day).padStart(2, "0") : "00";

    if (type === 1) return `${year}-${month}-${day}`; // วัน/เดือน/ปี
    if (type === 2) return `${year}-${month}-00`; // เดือน/ปี
    if (type === 3) return `${year}-00-00`; // ปี
    return "";
  };

  const toBuddhistDate = (date) => {
    if (!date) return "";
    const [year, month = "00", day = "00"] = date.split("-");
    if (!year) return "";
    return parseInt(year, 10) + 543 + month + day;
  };

  const submitForm = async () => {
    if (!validateStep2()) return;
    setLoading(true);
    try {
      const {
        idCard,
        laser,
        firstName,
        lastName,
        mobilePhone,
        birthDate,
        issuedStore,
      } = registerData;

      const payload = {
        idCard,
        laser,
        firstName,
        lastName,
        mobilePhone,
        birthDate: toBuddhistDate(birthDate),
        ...(issuedStore ? { issuedStore } : {}),
      };

      const clientAuth = process.env.REACT_APP_CLIENT_AUTH;
      const apiVerify = `${process.env.REACT_APP_API_URL}/api/users/verify`;

      const res = await axios.post(apiVerify, payload, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "X-Client-Auth": clientAuth,
          "x-user-id": userId,
          "Content-Type": "application/json",
          "Accept-Language": i18n.language.toUpperCase(),
        },
      });

      // console.log("📦 ส่ง verify payload:", payload);
      // console.log("📥 verify response:", res.data);

      const { statusCode, message, data } = res.data;
      switch (statusCode) {
        case 200:
          const { otpRef } = data || {};
          if (!otpRef) return setError("ไม่สามารถรับ OTP ได้");
          setRegisterData((prev) => ({
            ...prev,
            otpRef: otpRef,
          }));
          //setOtpPayload(payload);
          setShowOtp(true);
          break;
        case 400:
          switch (message) {
            case "INVALID_STORE_ID":
              setError(t("incorrect_store_code"));
              break;
            default:
              setError(t("dopa_error_code_400"));
          }
          break;
        case 409:
          setError(t("verify_registration_error_code_409"));
          break;
        case 503:
          setError(t("dopa_error_code_503"));
          break;
        default:
          setError(message);
      }
    } catch (err) {
      console.error("Error during registration:", err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const checkCitizenIdExists = async (idCard) => {
    //setLoading(true);
    try {
      const clientAuth = process.env.REACT_APP_CLIENT_AUTH;
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/users/check-member`;

      const headers = {
        Authorization: `Bearer ${authToken}`,
        "X-Client-Auth": clientAuth,
        "x-user-id": userId,
        "Content-Type": "application/json",
        "Accept-Language": i18n.language.toUpperCase(),
      };

      const response = await axios.post(apiUrl, { id: idCard }, { headers });
      const found = response.data?.data?.found;
      if (found === true) {
        setTimeout(() => {
          setError(t("verify_registration_error_code_409"));
          //setLoading(true);
        }, 2000);
        return true;
      }
    } catch (err) {
      console.error("API error:", err.response?.data || err.message);
      setError(t("verify_registration_error_code_500"));
    } finally {
      //setLoading(false);
    }
    return false;
  };

  const handleCitizenIdChange = async (e) => {
    const raw = e.target.value;
    const digitsOnly = raw.replace(/\D/g, "").slice(0, 13);

    setRegisterData((prev) => ({ ...prev, idCard: digitsOnly }));

    if (digitsOnly.length === 13 && !hasCheckedIdCard) {
      const exists = await checkCitizenIdExists(digitsOnly);
      if (!exists) {
        setHasCheckedIdCard(true);
      }
    } else {
      setHasCheckedIdCard(false);
    }
  };

  useEffect(() => {
    if (cardData) {
      // console.log("Payload ที่รับมาจาก Scan:", cardData);

      setLoading(true);

      setRegisterData((prev) => ({
        ...prev,
        idCard: cardData.cardNumber || "",
        firstName: cardData.firstName || "",
        lastName: cardData.lastName || "",
        birthDate: cardData.birthDate || "",
        laser: cardData.laserNumber || "",
        gender: cardData.gender || "",
        email: cardData.email || "",
      }));

      setTimeout(() => {
        setLoading(false);
      }, 1500);
    }
  }, [cardData]);

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    if (digits.length < 4) return digits;
    if (digits.length < 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  // Validate Form
  const validateStep1 = () => {
    const errors = {};
    if (registerData.idCard.length !== 13)
      errors.idCard = t("form_item_0_cannot_be_empty", {
        field: t("citizen_id"),
      });
    if (!registerData.firstName.trim())
      errors.firstName = t("form_item_0_cannot_be_empty", {
        field: t("first_name_on_citizen_id_card_placeholder"),
      });
    if (!registerData.lastName.trim())
      errors.lastName = t("form_item_0_cannot_be_empty", {
        field: t("last_name_on_citizen_id_card_placeholder"),
      });
    if (!registerData.gender)
      errors.gender = t("form_item_0_cannot_be_empty", { field: t("gender") });
    if (!registerData.birthDate)
      errors.birthDate = t("form_item_0_cannot_be_empty", {
        field: t("birth_date_on_citizen_id_card"),
      });
    if (!registerData.laser.trim())
      errors.laser = t("form_item_0_cannot_be_empty", {
        field: t("citizen_id_back"),
      });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep2 = () => {
    const errors = {};
    if (!/^\d{10}$/.test(registerData.mobilePhone))
      errors.mobilePhone = t("form_item_0_cannot_be_empty", {
        field: t("mobile_number"),
      });
    // if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(registerData.email))
    //   errors.email = t("form_item_0_cannot_be_empty", { field: t("email") });
    if (!registerData.terms) errors.terms = t("terms_error");
    // if (!registerData.pdpa) errors.pdpa = "กรุณายอมรับ PDPA";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const nextStep = () => {
    if (step === 1) {
      if (!validateStep1()) return;
    }
    if (step === 2) {
      if (!validateStep2()) return;
    }
    setStep((s) => Math.min(s + 1, 3));
  };
  const prevStep = () => setStep((s) => Math.max(s - 1, 1));

  const convertGender = (value) => {
    if (value === "M") return 1;
    if (value === "F") return 2;
    return null;
  };

  return (
    <div className="w-full min-h-screen bg-secondary-background-gray px-4 pt-10 pb-16 sm:px-6">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between mb-6 relative">
          <h1 className="text-3xl font-semibold text-left">
            {t("register_big_point_member")}
          </h1>

          <button
            onClick={() => navigate("/login")}
            aria-label="ปิด"
            className="absolute right-0 top-1/2 -translate-y-1/2 bg-gray-100 hover:bg-gray-200 text-gray-500 rounded-full p-2 shadow transition"
            style={{ minWidth: 40, minHeight: 40 }}
          >
            <svg
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              className="mx-auto"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 6l12 12M6 18L18 6"
              />
            </svg>
          </button>
        </div>
        {/* Progress Indicator */}
        <div className="flex gap-2">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className={`w-11 h-6 rounded-xl  font-semibold ${
                step === i
                  ? "bg-primary text-secondary-text-invert"
                  : "bg-overlay-5 text-secondary-text-gray"
              } ${
                step > i
                  ? "border-2 border-primary bg-secondary-background-gray"
                  : ""
              }`}
            >
              {step > i ? "✔" : i}
            </div>
          ))}
        </div>

        {/* Step 1 */}
        {step === 1 && (
          <>
            {/* citizen_id + Scan Front Card */}
            <div className="flex gap-2 items-end">
              <div className="flex-grow">
                <FormItem label={t("citizen_id")} error={formErrors.idCard}>
                  <Input
                    name="idCard"
                    value={formatCitizenId(registerData.idCard)}
                    placeholder={t("citizen_id_placeholder")}
                    onChange={handleCitizenIdChange}
                    className="text-left"
                    required
                  />
                </FormItem>
              </div>
              <div className="flex flex-col items-center mb-1">
                <label className="text-xs font-normal text-primary-text whitespace-nowrap mb-1">
                  {t("scan_front_id_card")}
                </label>
                <div
                  onClick={() => {
                    setScanSide("front");
                    setShowScanner(true);
                  }}
                  className="w-14 h-11 flex justify-center items-center bg-overlay-5 rounded-lg cursor-pointer hover:bg-overlay-7 transition"
                >
                  <img src={ScanIcon} alt={t("scan")} className="w-6 h-6" />
                </div>
              </div>
            </div>

            {/* ScanIdCard modal */}
            {/* {showScanner && (
              <ScanIdCard
                show={showScanner}
                onClose={() => setShowScanner(false)}
                bgImage="front"
                onOcr={(data) => {
                  console.log("📥 ได้ข้อมูลจาก OCR front:", data);
                  setRegisterData((prev) => ({
                    ...prev,
                    idCard: data.idCard || "",
                    firstname: data.firstName || "",
                    lastname: data.lastName || "",
                    gender: data.gender || "",
                    birthDate: data.birthDate || "",
                  }));
                  setBirthDateItem(data.birthDate || "");
                }}
              />
            )} */}

            <FormItem
              label={t("first_name_on_citizen_id_card_placeholder")}
              error={formErrors.firstName}
            >
              <Input
                name="firstName"
                value={registerData.firstName}
                placeholder={t("first_name_on_citizen_id_card_placeholder")}
                onChange={handleInputChange}
                className="text-left"
                required
              />
            </FormItem>
            <FormItem
              label={t("last_name_on_citizen_id_card_placeholder")}
              error={formErrors.lastName}
            >
              <Input
                name="lastName"
                className="text-left"
                value={registerData.lastName}
                placeholder={t("last_name_on_citizen_id_card_placeholder")}
                onChange={handleInputChange}
                required
              />
            </FormItem>
            <FormItem label={t("gender")} error={formErrors.gender}>
              <select
                name="gender"
                value={registerData.gender}
                onChange={handleInputChange}
                className="h-11 rounded-lg border px-3 w-full"
              >
                <option value="">{t("gender_placeholder")}</option>
                <option value="M">{t("male")}</option>
                <option value="F">{t("female")}</option>
              </select>
            </FormItem>

            <div className="w-full my-4">
              <FormItem
                label={t("birth_date_on_citizen_id_card")}
                error={formErrors.birthDate}
              >
                <div className="w-full space-y-2 py-2">
                  <RadioGroup
                    className="py-4 m-2"
                    items={[
                      { label: t("day_month_year"), value: 1 },
                      { label: t("month_year"), value: 2 },
                      { label: t("year"), value: 3 },
                    ]}
                    value={birthDateType}
                    onChange={(val) => {
                      setBirthDateType(val);
                      setBirthDateItem({}); // reset วันเดือนปี เมื่อเปลี่ยน type
                      setRegisterData((prev) => ({
                        ...prev,
                        birthDate: "", // reset ค่า
                        birthDateType: val,
                      }));
                    }}
                    size="xl"
                    fontWeight="md"
                  />

                  <DropdownDatepicker
                    className="mt-2"
                    name="birth_date"
                    value={birthDateItem}
                    mode={birthDateType}
                    onChange={(val) => {
                      setBirthDateItem(val);

                      const formattedBirthDate = formatBirthDate(
                        val,
                        birthDateType,
                      );

                      setRegisterData((prev) => ({
                        ...prev,
                        birthDate: formattedBirthDate,
                      }));

                      const isValid =
                        (birthDateType === 1 &&
                          val?.day &&
                          val?.month &&
                          val?.year) ||
                        (birthDateType === 2 && val?.month && val?.year) ||
                        (birthDateType === 3 && val?.year);

                      setFormErrors((prev) => ({
                        ...prev,
                        birthDate: isValid
                          ? ""
                          : t("form_item_0_cannot_be_empty", {
                              field: t("birth_date_on_citizen_id_card"),
                            }),
                      }));
                    }}
                  />
                </div>
              </FormItem>
            </div>
            {/* <FormItem
              label={t("birth_date_on_citizen_id_card")}
              error={formErrors.birthDate}
            >
              <input
                type="date"
                name="birthDate"
                value={birthDateItem}
                onChange={handleDateChange}
                className="h-11 rounded-lg border px-3 w-full"
                required
              />
            </FormItem> */}
            {/*  Scan Back Card */}
            <div className="flex gap-2 items-end">
              <div className="flex-grow">
                <FormItem
                  label={
                    <div className="flex items-center gap-2">
                      <span>{t("citizen_id_back")}</span>
                      <TooltipCard />
                    </div>
                  }
                  error={formErrors.laser}
                >
                  <Input
                    name="laser"
                    value={displayLaser}
                    placeholder={t("citizen_id_back_placeholder")}
                    onChange={handleInputChange}
                    required
                  />
                </FormItem>
              </div>
              <div className="flex flex-col items-center mb-1">
                <label className="text-xs font-normal text-primary-text whitespace-nowrap mb-1">
                  {t("scan_back_id_card")}
                </label>
                <div
                  onClick={() => {
                    setScanSide("back");
                    setShowScanner(true);
                  }}
                  className="w-14 h-11 flex justify-center items-center bg-overlay-5 rounded-lg cursor-pointer hover:bg-overlay-7 transition"
                >
                  <img src={ScanIcon} alt={t("scan")} className="w-6 h-6" />
                </div>
              </div>
              {/* ScanIdCard back modal */}
              {/* {showScanner && (
                <ScanIdBackCard
                  show={showScanner}
                  onClose={() => setShowScanner(false)}
                  bgImage="front"
                  onOcr={(data) => {
                    // console.log("📥 ได้ข้อมูลจาก back OCR:", data);
                    setRegisterData((prev) => ({
                      ...prev,
                      laser: data.laserNumber || "",
                      idCard: data.cardNumber || "",
                      firstName: data.firstname || "",
                      lastName: data.lastname || "",
                      gender: data.gender || "",
                      birthDate: data.birthDate || "",
                    }));
                    setBirthDateItem(data.birthDate || "");
                  }}
                />
              )} */}
              <ScanIdCard
                show={showScanner}
                onClose={() => setShowScanner(false)}
                side={scanSide}
                onOcr={(data, side) => {
                  if (side === "front") {
                    setRegisterData((prev) => ({
                      ...prev,
                      idCard: data.cardNumber || "",
                      firstName: data.firstname || "",
                      lastName: data.lastname || "",
                      gender: data.gender || "",
                      birthDate: data.birthDate || "",
                    }));
                    setBirthDateItem(data.birthDate || "");
                  } else if (side === "back") {
                    setRegisterData((prev) => ({
                      ...prev,
                      laser: data.laserNumber || "",
                    }));
                  }
                }}
              />
            </div>
          </>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <>
            <FormItem label={t("mobile_number")} error={formErrors.mobilePhone}>
              <Input
                name="mobilePhone"
                value={formatPhoneNumber(registerData.mobilePhone)}
                placeholder={t("mobile_number_placeholder")}
                onChange={handleInputChange}
                type="tel"
                required
              />
            </FormItem>
            <FormItem label={t("email")} error={formErrors.email}>
              <Input
                name="email"
                value={registerData.email}
                placeholder={t("email_placeholder")}
                onChange={handleInputChange}
                type="email"
              />
            </FormItem>
            <div className="w-full flex flex-col gap-2 bg-overlay-5 rounded-2xl mt-4 px-6 py-6 sm:px-2 xs:px-1">
              <div className="text-lg font-semibold text-primary-text">
                {t("for_staff")}
              </div>
              <FormItem label={t("for_staff")}>
                <Input
                  name="issuedStore"
                  value={registerData.issuedStore}
                  placeholder={t("store_code_placeholder")}
                  onChange={handleInputChange}
                  maxLength={5}
                />
              </FormItem>
            </div>

            <div className="flex flex-row gap-2 mt-2 text-left">
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="terms"
                  checked={registerData.terms}
                  onChange={handleInputChange}
                  className="peer appearance-none w-6 h-6 border-2 border-gray-400 rounded bg-white checked:bg-green-600 checked:border-green-600 transition-all"
                />
                <span
                  className="
    pointer-events-none
    absolute w-6 h-6 flex items-center justify-center
    text-white
    peer-checked:after:content-['✔']
    peer-checked:after:block
    peer-checked:after:text-xl
    peer-checked:after:text-center
    peer-checked:after:-mt-1
    "
                />
              </label>

              <span className="text-sm text-left">
                {t("terms_text_1")}{" "}
                <span
                  className="underline cursor-pointer"
                  onClick={() => setShowTermModal(true)}
                >
                  {t("terms_text_2")}
                </span>{" "}
                {t("terms_text_3")}
                <span
                  className="underline cursor-pointer"
                  onClick={() => setShowPrivacyModal(true)}
                >
                  {t("terms_text_4")}
                </span>
              </span>
            </div>
            {formErrors.terms && (
              <span className="text-red-500 text-sm mt-1 ml-8 block text-left">
                {formErrors.terms}
              </span>
            )}
            <div className="flex flex-row gap-2 text-left">
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="pdpa"
                  checked={registerData.pdpa}
                  onChange={handleInputChange}
                  className="peer appearance-none w-6 h-6 border-2 border-gray-400 rounded bg-white checked:bg-green-600 checked:border-6reen-500 transition-all"
                />
                <span
                  className="
    pointer-events-none
    absolute w-6 h-6 flex items-center justify-center
    text-white
    peer-checked:after:content-['✔']
    peer-checked:after:block
    peer-checked:after:text-xl
    peer-checked:after:text-center
    peer-checked:after:-mt-1
    "
                />
              </label>

              <span className="text-sm text-left">
                {t("pdpa_text_1")}{" "}
                <span
                  className="underline cursor-pointer"
                  onClick={() => setShowPdpaModal(true)}
                >
                  {t("pdpa_text_2")}
                </span>{" "}
                {t("pdpa_text_3")}
              </span>
            </div>
            {formErrors.pdpa && (
              <span className="text-red-500 text-sm mt-1 ml-7 block text-left">
                {formErrors.pdpa}
              </span>
            )}
          </>
        )}

        {/* Step 3 */}
        {step === 3 && <RegisterResultForm data={registerData} />}

        {/* Navigation */}
        <div className="flex justify-center items-center mt-6 gap-4 ">
          {step > 1 && (
            <div className="h-13">
              <Button
                shape="round"
                className="px-10 py-4 bg-gray-500 text-black"
                onClick={prevStep}
              >
                {t("go_back")}
              </Button>
            </div>
          )}
          {step < 2 ? (
            <div className="w-48 h-13">
              <DangerButton buttonName={t("next")} onClick={nextStep} />
            </div>
          ) : (
            <div className="h-13" style={{ margin: "10px" }}>
              <DangerButton
                buttonName={t("confirm_registration")}
                onClick={submitForm}
              />
            </div>
          )}
        </div>
        <LoadingBar loading={loading} label="Loading..." />

        {error && <ErrorModal error={error} onClose={() => setError(null)} />}
        {showPdpaModal && (
          <PdpaConsentRegister
            show={showPdpaModal}
            onAccept={(status) => {
              // ✅ set ค่า consentStatus แล้วเก็บใน otpPayload
              /*const newPayload = {
                ...registerData,
                consentStatus: status,
              };
              setOtpPayload(newPayload);*/

              setRegisterData((prev) => ({
                ...prev,
                pdpa: status === "C", // ถ้า "C" checked, ถ้า "N" ไม่ checked
                consentStatus: status,
              }));
              setShowPdpaModal(false);
            }}
            onClose={() => setShowPdpaModal(false)}
            //payload={pdpaPayload}
          />
        )}

        {showTermModal && (
          <TermRegister
            show={showTermModal}
            onAccept={(accepted) => {
              // ติ๊ก checkbox เงื่อนไขการสมัครอัตโนมัติ
              setRegisterData((prev) => ({
                ...prev,
                terms: !!accepted,
                contactPermission: accepted ? 1 : 2, // 1=Allow, 2=Not Allow
                contactPreference: accepted ? 7 : 0, // 0=Not Allow, 7=Email+Mobile+Mail
              }));

              // เก็บ contactPermission สำหรับ payload
              /*setOtpPayload((prev) => ({
                ...prev,
                contactPermission: parseInt(contactPermission),
              }));*/

              setShowTermModal(false);
            }}
            onClose={() => setShowTermModal(false)}
          />
        )}

        {showPrivacyModal && (
          <PrivacyPolicyRegister
            show={showPrivacyModal}
            onClose={() => setShowPrivacyModal(false)}
          />
        )}

        {/* ScanIdCard modal */}
      </div>
      {/* {showOtp && otpPayload && (
        <OtpForm
          {...otpPayload}
          onClose={() => setShowOtp(false)}
          onSubmit={() => {
            console.log("OTP complete!");
          }}
        />
      )} */}
      {showOtp && registerData && registerData.otpRef && (
        <OtpFormRegister
          registerData={registerData}
          onClose={() => setShowOtp(false)}
          onSubmit={() => {
            console.log("OTP complete!");
          }}
        />
      )}
    </div>
  );
}
